import { useCallback, useEffect, useState } from 'react';
import { loadCalendar, type CalendarApi } from './loadCalendar';

export { loadCalendar, type CalendarApi };

export interface CalendarModuleState {
  /** The module, or null while loading, after a failure, or while not wanted. */
  readonly calendar: CalendarApi | null;
  readonly loading: boolean;
  readonly failed: boolean;
  readonly retry: () => void;
}

/** Load the calendar module when `wanted` is true; nothing is fetched while it is false. */
export function useCalendarModule(wanted: boolean): CalendarModuleState {
  const [calendar, setCalendar] = useState<CalendarApi | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!wanted) return;
    let cancelled = false;
    loadCalendar().then(
      (loaded) => {
        if (cancelled) return;
        setCalendar(loaded);
        setFailed(false);
      },
      (error: unknown) => {
        console.warn('[important dates] calendar module failed to load', error);
        if (!cancelled) setFailed(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [wanted, attempt]);
  const retry = useCallback(() => {
    setFailed(false);
    setAttempt((count) => count + 1);
  }, []);
  return { calendar, loading: wanted && calendar === null && !failed, failed, retry };
}
